import { useQuery } from '@tanstack/react-query';
import { useDataChainId } from '@ui/hooks/datachain';
import { getDexAggregator } from '@ui/services/contract/contract-instances/getDexAggregator';
import { Token } from '@ui/types/token';
import { Address,formatUnits, parseUnits, zeroAddress } from 'viem';
import { usePublicClient, useAccount } from 'wagmi';

import { encodeAdapterData,getExchangePools } from '../config';

export const useExchangeQuote = ({
  inputToken,
  outputToken,
  inputAmount,
}: {
  inputToken?: Token;
  outputToken?: Token;
  inputAmount: string;
}) => {
  const chainId = useDataChainId();
  const publicClient = usePublicClient({ chainId });
  const { address } = useAccount();

  const isEnabled = 
    !!inputToken?.address &&
    !!outputToken?.address &&
    !!inputAmount &&
    !!inputToken.decimals &&
    !!outputToken.decimals &&
    parseFloat(inputAmount) > 0;

  return useQuery({
    queryKey: [
      'exchange-quote',
      chainId,
      inputToken?.address,
      outputToken?.address,
      inputAmount,
      !!publicClient
    ],
    queryFn: async () => {
      console.log('[useExchangeQuote] Starting quote fetch', { 
        chainId, 
        inputToken: inputToken?.symbol,
        inputTokenAddr: inputToken?.address,
        outputToken: outputToken?.symbol, 
        outputTokenAddr: outputToken?.address,
        inputAmount,
        publicClient: !!publicClient 
      });

      if (!publicClient) {
        console.warn('[useExchangeQuote] publicClient is missing');
        
return null;
      }

      if (
        (!inputToken?.address) ||
        (!outputToken?.address) ||
        (!inputToken.decimals) ||
        !inputAmount
      ) {
        console.warn('[useExchangeQuote] Missing required params');
        
return null;
      }

      const amountIn = parseUnits(inputAmount, inputToken.decimals);
      if (amountIn === 0n) return null;

      const dexAggregator = getDexAggregator({
        chainId,
        publicClient: publicClient as any,
      });

      const pools = getExchangePools(chainId, inputToken, outputToken);
      if (!pools || pools.length === 0) {
        console.warn('[useExchangeQuote] No pools found for pair');
        
return null;
      }

      // Encode router address into data for each pool
      const candidates = pools.map((pool) => ({
        adapter: pool.adapter,
        data: encodeAdapterData(pool, address),
      }));

      // Fee is 0.5% of input
      const fee = 0n;
      const amountAfterFee = amountIn - fee;

      try {
        console.log('[useExchangeQuote] Calling findBestPath', {
          amountIn: amountAfterFee,
          tokenIn: inputToken.address,
          tokenOut: outputToken.address,
          candidates
        });

        const result = await dexAggregator.read.findBestPath([
          amountAfterFee,
          inputToken.address as Address,
          outputToken.address as Address,
          candidates,
        ]);

        const [bestCandidate, bestAmountOut] = result;
        console.log('[useExchangeQuote] Result', { bestCandidate, bestAmountOut });

        return {
          bestCandidate,
          amountOut: bestAmountOut,
          formattedAmountOut: formatUnits(bestAmountOut, outputToken.decimals),
        };
      } catch (error) {
        console.error('[useExchangeQuote] Error fetching quote:', error);
        
return null;
      }
    },
    enabled: isEnabled,
    refetchInterval: 10000, // Refresh every 10 seconds
  });
};
